import { computeRunProgress } from "utils/stressProfileProgress";
import { describeStep, formatTotalDuration, sumStepDurations } from "utils/stressProfileFormat";
import { formatRelativeTime } from "utils/helper";
import { computeWaterEventMarkers, describeWaterEvent, formatWaterEventDelta } from "utils/waterEvents";
import { campaignIdFromRunId, conditionSeqFromRunId } from "utils/calibrationCampaign";

// Visually distinguishes each segment's role in the plan - text/notes
// below the bar carry the same information in words, not color alone.
const SEGMENT_CLASSES = {
  done: "bg-emerald-500",
  current: "bg-emerald-100",
  upcoming: "bg-slate-200",
  stopped: "bg-rose-200",
  skipped: "bg-slate-100 border border-dashed border-slate-300",
};

const CURRENT_FILL_CLASSES = {
  ok: "bg-emerald-500",
  overrun: "bg-amber-500",
  stale: "bg-slate-400",
};

function formatClock(seconds) {
  // formatTotalDuration always shows "Xh Ym" - fine for a countdown/elapsed
  // display too, just reusing the one duration formatter this codebase has
  // rather than inventing a second "Xm" - style one.
  return formatTotalDuration(Math.max(0, seconds));
}

function statusNote({ phase, run, result, isActive }) {
  switch (phase) {
    case "no-plan":
      return "Planned steps weren't recorded for this run - showing raw status only.";
    case "rejected":
      return "The controller rejected this run's start command.";
    case "waiting":
      return "Waiting for the controller's first report…";
    case "ended-no-data":
      return "This run ended before any telemetry arrived for it.";
    case "unknown-position":
      return "The controller hasn't reported a valid step position yet.";
    case "finishing":
      return "Finishing up — the controller is returning to a safe resting state.";
    case "completed":
      return `Completed${run.ended_at ? ` ${formatRelativeTime(run.ended_at)}` : ""}.`;
    case "ended-early": {
      const stepLabel = `step ${result.currentStepIndex + 1} of ${run.steps.length}`;
      return result.stoppedByOperator
        ? `Stopped by operator at ${stepLabel}.`
        : `Ended at ${stepLabel}.`;
    }
    case "running": {
      const parts = [];
      if (result.isStale) {
        parts.push(
          `No report from the controller for ${formatClock(
            (Date.now() - Date.parse(run.progress.last_reported_at)) / 1000
          )}. Position shown as of ${new Date(run.progress.last_reported_at).toLocaleTimeString()}.`
        );
      } else if (result.isOverrun) {
        parts.push(
          `This step is running ${formatClock(
            result.overrunSeconds
          )} longer than planned. The controller moves on when its own timer finishes (it pauses while powered off).`
        );
      }
      return parts.join(" ") || null;
    }
    default:
      return null;
  }
}

function CampaignRunCard({ run, isActive }) {
  const hold = (Array.isArray(run.steps) ? run.steps : [])[0];
  const name = (run.profile_name || "").replace(/^Calibration: /, "") || "calibration";
  // null for a campaign's single run (since 21 Oct 2026); a number for an older campaign's per-condition run.
  const seq = conditionSeqFromRunId(run.run_id);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="campaign-run-card">
      <p className={`text-xs font-bold uppercase tracking-wide ${isActive ? "text-emerald-600" : "text-slate-500"}`}>
        {isActive ? "Now running" : "Last run"}
      </p>
      <p className="mt-0.5 text-sm font-semibold text-slate-900">
        {seq === null ? "Calibration campaign" : `Condition ${seq + 1} of the calibration campaign`} &ldquo;{name}&rdquo;
        {hold ? `, chamber ${seq === null && isActive ? "now " : ""}held at ${hold.temp_c} °C / ${hold.rh_pct} %RH` : ""}
      </p>
      <p className="text-xs text-slate-500">
        Started {new Date(run.started_at).toLocaleString()}
        {run.ended_at ? ` · ended ${new Date(run.ended_at).toLocaleString()}` : ""}.{" "}
        {seq === null
          ? "One run for the whole campaign: the campaign sets the chamber for each condition in turn and ends the run after the last one."
          : "The campaign ends this run as soon as this condition\u2019s water steps are done, so it never runs to the end of its hold."}
      </p>
    </div>
  );
}

export default function RunProgressTimeline({ controller, pumpConfig = null }) {
  const activeRun = controller?.active_run ?? null;
  const lastRun = controller?.last_run ?? null;
  const run = activeRun ?? lastRun;
  if (!run) return null;
  // A calibration campaign's condition runs are held by the campaign and ended by it when the condition's water
  // steps are done: their hold step's percentage would read as an unfinished run.
  if (campaignIdFromRunId(run.run_id) !== null) return <CampaignRunCard run={run} isActive={!!activeRun} />;

  const isActive = !!activeRun;
  const steps = Array.isArray(run.steps) ? run.steps : [];
  const progress = run.progress ?? null;
  const latestCommand = activeRun?.latest_command ?? null;
  const startRejected = !!(
    latestCommand &&
    latestCommand.command === "start_run" &&
    latestCommand.acknowledged &&
    latestCommand.ack_status &&
    latestCommand.ack_status !== "ok"
  );

  const result = computeRunProgress({
    steps,
    progress,
    isActive,
    stoppedByOperator: !!run.stopped_by_operator,
    startRejected,
    nowMs: Date.now(),
  });
  const { phase, segments, overallPct } = result;
  const runForNote = { ...run, steps, progress };
  const note = statusNote({ phase, run: runForNote, result, isActive });

  const totalPlannedSeconds = sumStepDurations(steps);
  const hasBar = segments.length > 0 && phase !== "no-plan";
  // Planned pump operations, placed by minutes since the run started on the same planned length
  // as the bar. A run's own plan, so a later edit of the saved profile never moves them.
  const waterEvents = Array.isArray(run.water_events) ? run.water_events : [];
  const waterMarkers = computeWaterEventMarkers(waterEvents, totalPlannedSeconds);
  const showPositionMarker = phase === "running" || phase === "finishing";
  const currentIndex = typeof result.currentStepIndex === "number" ? result.currentStepIndex : null;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p
            className={`text-xs font-bold uppercase tracking-wide ${
              isActive ? "text-emerald-600" : "text-slate-500"
            }`}
          >
            {isActive ? "Now running" : "Last run"}
          </p>
          <p className="mt-0.5 truncate font-mono text-sm font-semibold text-slate-900">
            {run.run_id}
            {run.profile_name ? ` (${run.profile_name})` : ""}
          </p>
          <p className="text-xs text-slate-500">
            Started {new Date(run.started_at).toLocaleString()}
            {totalPlannedSeconds > 0 ? ` · plan ${formatTotalDuration(totalPlannedSeconds)}` : ""}
          </p>
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold text-slate-900">
            {overallPct == null ? "—" : `${Math.floor(overallPct)}%`}
          </p>
          {currentIndex != null && (
            <p className="text-xs text-slate-500">
              Step {currentIndex + 1} of {steps.length}
            </p>
          )}
          {phase === "running" && !result.isStale && result.remainingSeconds != null && (
            <p className="text-xs text-slate-500">
              {result.remainingIsLowerBound ? "at least " : "~"}
              {formatClock(result.remainingSeconds)} left
            </p>
          )}
        </div>
      </div>

      {hasBar && (
        <div className="mt-3">
          <div
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={overallPct == null ? undefined : Math.floor(overallPct)}
            aria-valuetext={
              currentIndex != null
                ? `Step ${currentIndex + 1} of ${steps.length}${
                    overallPct == null ? "" : `, ${Math.floor(overallPct)}% complete`
                  }`
                : undefined
            }
            className="flex h-8 gap-0.5 overflow-hidden rounded-lg"
          >
            {segments.map((segment) => {
              const isCurrent = segment.index === currentIndex && showPositionMarker;
              const fillClass = isCurrent
                ? result.isStale
                  ? CURRENT_FILL_CLASSES.stale
                  : result.isOverrun
                  ? CURRENT_FILL_CLASSES.overrun
                  : CURRENT_FILL_CLASSES.ok
                : null;
              return (
                <div
                  key={segment.index}
                  data-testid={`run-progress-segment-${segment.index}`}
                  data-status={segment.status}
                  title={describeStep(segment.step)}
                  aria-label={describeStep(segment.step)}
                  style={{ flexGrow: segment.weight > 0 ? segment.weight : 1, flexBasis: 0, minWidth: 6 }}
                  className={`relative ${SEGMENT_CLASSES[segment.status] || SEGMENT_CLASSES.upcoming}`}
                >
                  {(segment.status === "current" || segment.status === "stopped") && (
                    <div
                      className={`absolute inset-y-0 left-0 ${fillClass || SEGMENT_CLASSES.current}`}
                      style={{ width: `${segment.fillPct}%` }}
                    />
                  )}
                </div>
              );
            })}
          </div>
          {waterMarkers.length > 0 && (
            <div className="relative mt-0.5 h-8" data-testid="run-progress-water-events">
              {waterMarkers.map((marker) => (
                <span
                  key={marker.index}
                  data-testid={`run-progress-water-event-${marker.index}`}
                  title={describeWaterEvent(marker.event, pumpConfig || undefined)}
                  aria-label={describeWaterEvent(marker.event, pumpConfig || undefined)}
                  style={{ left: `${marker.leftPct}%` }}
                  className={`absolute top-0 flex -translate-x-1/2 flex-col items-center leading-none ${
                    marker.event.action === "drain" ? "text-amber-600" : "text-blue-600"
                  }`}
                >
                  <span aria-hidden="true" className="text-xs">
                    {marker.event.action === "drain" ? "▼" : "▲"}
                  </span>
                  {/* The symbol alone did not say what it does: "+1 cm" (fill) / "−1 cm" (drain). */}
                  <span
                    aria-hidden="true"
                    data-testid={`run-progress-water-event-${marker.index}-delta`}
                    className="mt-0.5 whitespace-nowrap text-[10px] font-semibold"
                  >
                    {formatWaterEventDelta(marker.event)}
                  </span>
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {currentIndex != null && steps[currentIndex] && (
        <p className="mt-2 text-sm text-slate-800">
          Step {currentIndex + 1}: {describeStep(steps[currentIndex])}
        </p>
      )}
      {currentIndex != null && steps[currentIndex + 1] && phase === "running" && (
        <p className="text-xs text-slate-500">Next: {describeStep(steps[currentIndex + 1])}</p>
      )}

      {note && (
        <p
          role="status"
          className={`mt-2 text-xs ${
            phase === "running" && (result.isOverrun || result.isStale)
              ? "text-amber-700"
              : "text-slate-500"
          }`}
        >
          {note}
        </p>
      )}

      {steps.length > 0 && (
        <details className="mt-3">
          <summary className="cursor-pointer text-xs font-semibold text-slate-500">
            All steps ({steps.length})
          </summary>
          <ol className="mt-2 list-decimal space-y-1 pl-5 text-xs text-slate-700">
            {steps.map((step, index) => (
              <li key={index} className={index === currentIndex ? "font-semibold text-slate-900" : ""}>
                {describeStep(step)}
              </li>
            ))}
          </ol>
        </details>
      )}
    </div>
  );
}
